import { HORIZON, type Edit } from "./types";
import type { EditStore } from "./editStore";
import type { SessionLog } from "./log";

// Аниматор времени (GDD 4.2 / 9.3).
//  target: следующая цель, queue: остальные числа из фразы.
//  t ведётся к цели через все промежуточные моменты. На каждом пересечении
//  целого момента — щелчок + события момента (вперёд или задом наперёд).
//  Заморозка = очередь пуста и t равно целому.

const MS_NEIGHBOUR = 450; // соседний момент
const MS_JUMP_PER = 120; // момент при скачке
const MS_JUMP_CAP = 1500; // весь скачок не длиннее
const MS_QUEUE_PER = 200; // при очереди из трёх и более чисел

export interface AnimatorCallbacks {
  /** Пересечён целый момент. dir = +1 вперёд, -1 назад. */
  onMomentCross(moment: number, dir: 1 | -1): void;
  /** Мир пришёл в момент и встал (заморозка). */
  onFreeze(moment: number): void;
  /** Мир начал движение к цели. */
  onMoveStart(from: number, to: number, dir: 1 | -1, jump: boolean): void;
  /** Повтор текущего числа: дрожание. reps — сколько раз подряд. */
  onRepeatShake(moment: number, reps: number): void;
  /** Число больше горизонта. */
  onOverHorizon(n: number): void;
  /** Откат отменил правки — их предметы уезжают на место. */
  onEditsUndone(removed: Edit[]): void;
}

export class TimeAnimator {
  t = 0;
  private queue: number[] = [];
  private target: number | null = null;
  private jump = false;
  private segFrom = 0;
  private segTo = 0;
  private segElapsed = 0;
  private segDur = MS_NEIGHBOUR;
  private lastRepeatN: number | null = null;
  private repeatCount = 0;
  private frozenAnnounced = true;

  constructor(
    private edits: EditStore,
    private cb: AnimatorCallbacks,
    private log: SessionLog,
  ) {}

  get moving(): boolean {
    return this.target !== null || this.queue.length > 0 || !Number.isInteger(this.t);
  }

  /** Момент, если мир заморожен на целом числе, иначе null. */
  get frozenMoment(): number | null {
    return this.moving ? null : this.t;
  }

  get pending(): number {
    return this.queue.length + (this.target !== null ? 1 : 0);
  }

  /** Принять числа из распознавания (по порядку). */
  enqueue(numbers: number[]): void {
    for (const raw of numbers) {
      const n = Math.round(raw);
      if (n > HORIZON) {
        this.cb.onOverHorizon(n);
        this.log.add("over_horizon", { n });
        continue;
      }
      if (n < 0) continue;

      const atRest = !this.moving;
      if (atRest && n === this.t) {
        this.repeatCount = this.lastRepeatN === n ? this.repeatCount + 1 : 1;
        this.lastRepeatN = n;
        this.cb.onRepeatShake(n, this.repeatCount);
        this.log.add("repeat_shake", { n, reps: this.repeatCount });
        continue;
      }
      this.repeatCount = 0;
      this.lastRepeatN = null;

      if (this.queue.length < 5) this.queue.push(n);
      this.log.add("queue", { queue: this.queue.slice(), target: this.target });
    }
    if (this.target === null) this.pickNextTarget();
  }

  private pickNextTarget() {
    const next = this.queue.shift();
    if (next === undefined) return;
    this.target = next;
    const dir: 1 | -1 = next >= this.t ? 1 : -1;
    const dist = Math.abs(next - Math.round(this.t));
    this.jump = dist >= 2;
    this.log.add(dir === 1 ? "move_forward" : "move_back", { from: this.t, to: next, jump: this.jump });
    if (this.jump) this.log.add("jump", { from: this.t, to: next, dist });
    this.cb.onMoveStart(this.t, next, dir, this.jump);
    this.frozenAnnounced = false;
    this.startSegment();
  }

  private startSegment() {
    if (this.target === null) return;
    const dir = this.target >= this.t ? 1 : -1;
    this.segFrom = this.t;
    // Ведём к следующему целому в сторону цели.
    const nextInt = dir === 1 ? Math.floor(this.t) + 1 : Math.ceil(this.t) - 1;
    this.segTo = nextInt;
    this.segElapsed = 0;
    const distToTarget = Math.abs(this.target - Math.round(this.t));
    if (this.pending >= 2) this.segDur = MS_QUEUE_PER;
    else if (distToTarget >= 2) this.segDur = Math.max(MS_JUMP_PER, MS_JUMP_CAP / distToTarget);
    else this.segDur = MS_NEIGHBOUR;
  }

  update(dtMs: number): void {
    if (this.target === null) {
      if (!this.frozenAnnounced && Number.isInteger(this.t)) {
        this.frozenAnnounced = true;
        this.log.add("freeze", { moment: this.t });
        this.cb.onFreeze(this.t);
      }
      return;
    }

    this.segElapsed += dtMs;
    const frac = Math.min(1, this.segElapsed / this.segDur);
    const prevT = this.t;
    this.t = this.segFrom + (this.segTo - this.segFrom) * frac;

    // Пересечение целого момента?
    const dir: 1 | -1 = this.segTo >= this.segFrom ? 1 : -1;
    const crossed =
      dir === 1
        ? Math.floor(this.t + 1e-9) > Math.floor(prevT + 1e-9)
        : Math.ceil(this.t - 1e-9) < Math.ceil(prevT - 1e-9);

    if (frac >= 1) {
      this.t = this.segTo;
      const m = Math.round(this.t);
      this.cb.onMomentCross(m, dir);
      this.log.add("moment_enter", { moment: m, dir });
      if (dir === -1) {
        const removed = this.edits.rollbackTo(m);
        if (removed.length) {
          this.log.add("edit_undone", { removed: removed.map((e) => ({ id: e.objectId, tick: e.tick })) });
          this.cb.onEditsUndone(removed);
        }
      }
      if (m === this.target) {
        this.target = null;
        this.pickNextTarget();
      } else {
        this.startSegment();
      }
    } else if (crossed) {
      // (для крупного шага внутри одного сегмента — на практике сегмент = 1 момент)
      const m = dir === 1 ? Math.floor(this.t) : Math.ceil(this.t);
      this.cb.onMomentCross(m, dir);
    }
  }

  /** Прямой переход (панель фасилитатора). */
  jumpTo(n: number): void {
    this.queue = [];
    this.target = null;
    this.enqueue([n]);
  }

  reset(): void {
    this.t = 0;
    this.queue = [];
    this.target = null;
    this.jump = false;
    this.repeatCount = 0;
    this.lastRepeatN = null;
    this.frozenAnnounced = false;
  }
}
